import { LumiDB } from "@lumidb/lumidb";
import * as THREE from "three";
import { type Annotations } from "./annotations";
import { App } from "./app";
import type { CrossSection } from "./cross-section";
import type { EarthControls } from "./earth-controls";
import { History } from "./history";
import { HUD } from "./hud";
import type { LOD } from "./lod";
import type { Prefs } from "./prefs";
import { Project, type ProjectTask } from "./project";
import { app, prefs } from "./state";
import type { BasemapType, Terrain } from "./terrain";
import { PersistedCamera, setArray, STORAGE_KEY } from "./utils";
import { type Viewer } from "./viewer";

const MAX_TASKS = 10;

type SessionState = {
    app: Omit<typeof app.state, "projects" | "datasets" | "pointclouds" | "rasters" | "attachments" | "collections">;
    camera: PersistedCamera;
};

type LocalState = {
    prefs: typeof prefs.state;
};

type ComposedViewer = Viewer & {
    annotations: Annotations;
    controls: EarthControls;
    terrain: Terrain;
    lod: LOD;
};

type ComposedCrossSection = CrossSection & {
    lod: LOD;
};

export class Scene {
    private readonly app: App;
    private readonly viewer: ComposedViewer;
    private readonly prefs: Prefs;
    private readonly project: Project;
    private readonly hud: HUD;
    private readonly history: History;
    private readonly lumidb: LumiDB;
    private readonly crossSection: ComposedCrossSection;

    private pollController?: AbortController;
    private handledTasks: Set<string> | null = null;

    constructor(
        app: App,
        project: Project,
        viewer: ComposedViewer,
        prefs: Prefs,
        hud: HUD,
        history: History,
        lumidb: LumiDB,
        crossSection: ComposedCrossSection,
    ) {
        this.app = app;
        this.project = project;
        this.viewer = viewer;
        this.prefs = prefs;
        this.hud = hud;
        this.history = history;
        this.lumidb = lumidb;
        this.crossSection = crossSection;
    }

    private sceneDatasetIds(projectId: string) {
        return Object.keys(this.project.sceneDatasets(projectId));
    }

    private initApp(projectId: string) {
        setArray(this.app.state.visible, this.sceneDatasetIds(projectId));

        const ext = this.project.extent(projectId);
        const minZ = Math.floor(ext.min.z);
        const maxZ = Math.ceil(ext.max.z);
        this.app.state.zRange[0] = minZ;
        this.app.state.zRange[1] = maxZ;
        this.app.state.elevationRange[0] = minZ;
        this.app.state.elevationRange[1] = maxZ;
    }

    private loadLocalState() {
        const state = localStorage.getItem(STORAGE_KEY);
        if (state) {
            const { prefs: prefsState } = JSON.parse(state) as LocalState;
            Object.assign(this.prefs.state, prefsState);
        }
    }

    private loadSessionState(projectId: string) {
        const params = new URLSearchParams(window.location.search);

        let state = params.get("state");
        if (state) {
            state = atob(state);
        } else {
            state = sessionStorage.getItem(STORAGE_KEY);
        }

        if (state) {
            const { app: appState, camera } = JSON.parse(state) as SessionState;

            if (appState) {
                Object.assign(this.app.state, appState);

                // Add new scene datasets to the visible list
                for (const datasetId of this.sceneDatasetIds(projectId)) {
                    if (!this.app.state.visible.includes(datasetId)) {
                        this.app.state.visible.push(datasetId);
                    }
                }
            } else {
                this.initApp(projectId);
            }

            // `far` might be missing in old perscameras
            if (camera.type === "ortho" && "far" in camera) {
                this.viewer.camera = new THREE.OrthographicCamera();
                this.viewer.camera.left = camera.left;
                this.viewer.camera.right = camera.right;
                this.viewer.camera.top = camera.top;
                this.viewer.camera.bottom = camera.bottom;
                this.viewer.camera.far = camera.far;
                this.viewer.camera.up.set(0, 0, 1);
            }

            this.viewer.camera.position.copy(camera.position);
            this.viewer.camera.rotation.copy(camera.rotation);
            this.viewer.updateCameraNearFar();
            this.viewer.camera.updateMatrixWorld();
        } else {
            this.initApp(projectId);
            this.viewer.controls.focus();
        }

        window.history.replaceState(null, "", window.location.pathname);
    }

    private loadFeatures(projectId: string) {
        this.project.getFeatures(projectId, "none").then((features) => {
            this.viewer.annotations.addFeatures(features);
            this.viewer.annotations.eval();
        });
    }

    private loadTerrain(projectId: string) {
        const key = `lumidb-basemap-type:${projectId}`;
        const type = localStorage.getItem(key) as BasemapType | null;
        if (type === "custom") {
            const p = this.project.get(projectId);
            if (!p.basemap_template_url) return false;
            this.viewer.terrain.setBaseUrl(p.basemap_template_url);
        } else if (type !== null) {
            this.viewer.terrain.setType(type);
        } else {
            this.viewer.terrain.setType("auto");
        }
    }

    private async fetchTasks(projectId: string, signal: AbortSignal) {
        const opts = { limit: MAX_TASKS, signal };
        const items = await this.project.listTasks(projectId, opts);

        if (this.hud.state.tasks.items === null) {
            this.hud.state.tasks.items = items;
        } else {
            setArray(this.hud.state.tasks.items, items);
        }
    }

    private async handleReadyImport(projectId: string, task: ProjectTask, signal: AbortSignal) {
        const wasEmpty = this.isEmpty(projectId);
        const previousIds = this.sceneDatasetIds(projectId);

        try {
            await Promise.all([this.app.loadDatasets(signal), this.project.loadProjects(signal)]);

            const currentIds = this.sceneDatasetIds(projectId);
            for (const id of currentIds) {
                if (!previousIds.includes(id)) {
                    this.app.state.visible.push(id);
                }
            }

            const extent = this.project.extent(projectId);
            this.app.adjustRanges(extent);

            if (wasEmpty && currentIds.length > 0) {
                this.viewer.controls.focus();
            } else {
                this.viewer.terrain.update();
            }
        } catch (err) {
            signal.throwIfAborted();
            console.error(`Failed to handle ready import`, err);
        }
    }

    private async handleReadyExport(task: ProjectTask, signal: AbortSignal) {
        const exportId = task.output?.["export_id"];
        if (typeof exportId !== "string") return;

        const { url } = await this.lumidb.downloadExportUrl(exportId, signal);
        const link = document.createElement("a");

        link.href = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    private isEmpty(projectId: string) {
        return this.project.isEmpty(projectId) && this.viewer.annotations.isEmpty();
    }

    private async handleTasks(projectId: string, signal: AbortSignal) {
        const items = this.hud.state.tasks.items;
        if (items === null) return;

        // Tasks that start ready don't need to be handled
        if (this.handledTasks === null) {
            const ready = items.filter((task) => task.status === "ready");
            this.handledTasks = new Set(ready.map((task) => task.task_id));
            return;
        }

        for (const task of items) {
            const handled = this.handledTasks?.has(task.task_id);
            if (task.status !== "ready" || handled) {
                continue;
            }

            switch (task.task_type) {
                case "import":
                case "raster_import": {
                    await this.handleReadyImport(projectId, task, signal);
                    break;
                }
                case "export": {
                    await this.handleReadyExport(task, signal);
                    break;
                }
            }

            if (signal.aborted) return;
            this.handledTasks?.add(task.task_id);
        }
    }

    public async pollTasks() {
        this.pollController?.abort();
        const controller = new AbortController();
        this.pollController = controller;
        const { signal } = controller;

        try {
            const p = this.project.get();
            const timeouts = [0, 1, 2, 4];

            while (!signal.aborted) {
                const timeout_ms = (timeouts.shift() ?? 8) * 1000;
                await new Promise((resolve) => setTimeout(resolve, timeout_ms));
                await this.fetchTasks(p.project_id, signal);
                await this.handleTasks(p.project_id, signal);

                const tasks = this.hud.state.tasks.items;
                if (tasks === null) continue;

                const statuses = tasks.map((t) => t.status);
                const doneStatuses = ["ready", "failed", "cancelled"];
                if (statuses.every((s) => doneStatuses.includes(s))) {
                    break;
                }
            }
        } catch (err) {
            if (!signal.aborted) throw err;
        } finally {
            if (this.pollController === controller) {
                this.pollController = undefined;
            }
        }
    }

    public open(projectId: string) {
        this.pollTasks();
        this.loadLocalState();
        this.loadSessionState(projectId);

        this.viewer.lod.eval();
        this.loadFeatures(projectId);
        this.loadTerrain(projectId);
    }

    public close() {
        this.pollController?.abort();
        this.pollController = undefined;
        this.handledTasks = null;

        this.viewer.lod.unloadTables();
        this.crossSection.lod.unloadTables();
        this.viewer.terrain.reset();
        this.viewer.annotations.reset();
        this.app.reset();
        this.hud.reset();
        this.history.reset();

        sessionStorage.removeItem(STORAGE_KEY);
    }
}
